import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonCredentialDialog from './PersonCredentialDialog';
import { getPeopleCredentials, deletePersonCredential } from '../../services/userService';
import { CREDENTIAL_GRID_COLUMNS } from '../../constants/personCredentialFormConstants';

const PersonCredentialsTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const canEditPerson = getFullPermissions('globals', 'People', 'any').Edit;
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadCredentials = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleCredentials(personId),
      onSuccess: (data) => setCredentials(data),
    });
  }, [dispatch, personId]);

  const handleAdd = useCallback(() => {
    setEditingCredential(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingCredential(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingCredential(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadCredentials();
  }, [handleFormClose, loadCredentials]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${row.Type || 'credential'}" record?`,
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () => deletePersonCredential(row.ptsCredentialID),
            onSuccess: () => {
              dispatch(notify('Credential deleted successfully', 'success'));
              loadCredentials();
            },
          });
        },
        'Delete Credential'
      );
    },
    [dispatch, requestConfirm, loadCredentials]
  );

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  return (
    <Box className="person-names-tab__grid-wrap">
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Credential"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={credentials}
        columns={CREDENTIAL_GRID_COLUMNS}
        getRowId={(row) => row.ptsCredentialID}
        loading={loading}
        autoHeight
        toolbar={{
          showSearch: true,
          showColumnSelector: true,
          showFilter: true,
          showDensity: true,
          showExport: true,
          printTitle: 'Credentials',
        }}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonCredentialDialog
          open={formOpen}
          personId={personId}
          credential={editingCredential}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PersonCredentialsTab;
